import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Users, Clock, FileText, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../../lib/api';

function FacultyReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedFaculty, setExpandedFaculty] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await api.get('/admin/faculty-reports');
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching faculty reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDuration = (mins) => {
    if (!mins || mins === 0) return '-';
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    const remaining = Math.round(mins % 60);
    return `${hours}h ${remaining}m`;
  };

  const toggleExpanded = (facultyId) => {
    setExpandedFaculty(expandedFaculty === facultyId ? null : facultyId);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="faculty-reports-title">
            Faculty Reports
          </h1>
          <p className="text-gray-600">Track faculty portal usage, activity, and session history</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Faculty</p>
                <p className="text-2xl font-bold" data-testid="total-faculty-count">{reports.length}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Activity className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Logins</p>
                <p className="text-2xl font-bold">{reports.reduce((sum, r) => sum + r.total_logins, 0)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Tests Created</p>
                <p className="text-2xl font-bold">{reports.reduce((sum, r) => sum + r.total_tests_created, 0)}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Portal Time</p>
                <p className="text-2xl font-bold">{formatDuration(reports.reduce((sum, r) => sum + r.total_session_mins, 0))}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Faculty Table */}
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="faculty-reports-table">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Faculty</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Logins</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Tests Created</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total Actions</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Session</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Total Time</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reports.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                      <p>No faculty users found</p>
                    </td>
                  </tr>
                ) : (
                  reports.map((report) => (
                    <React.Fragment key={report.faculty_id}>
                      <tr className="hover:bg-gray-50" data-testid={`faculty-row-${report.faculty_id}`}>
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-medium text-gray-900">{report.name}</p>
                            <p className="text-sm text-gray-500">{report.email}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDate(report.last_login)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Badge variant="secondary">{report.total_logins}</Badge>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Badge variant={report.total_tests_created > 0 ? 'default' : 'secondary'}>
                            {report.total_tests_created}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">
                          {report.total_actions}
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">
                          {formatDuration(report.avg_session_mins)}
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">
                          {formatDuration(report.total_session_mins)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpanded(report.faculty_id)}
                            data-testid={`expand-faculty-${report.faculty_id}`}
                          >
                            {expandedFaculty === report.faculty_id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </Button>
                        </td>
                      </tr>
                      {expandedFaculty === report.faculty_id && (
                        <tr>
                          <td colSpan={8} className="px-6 py-4 bg-gray-50">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {/* Tests Created */}
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-3">Tests Created</h4>
                                {report.tests.length === 0 ? (
                                  <p className="text-sm text-gray-500">No tests created yet</p>
                                ) : (
                                  <div className="space-y-2">
                                    {report.tests.map((test) => (
                                      <div key={test.id} className="flex items-center justify-between p-2 bg-white rounded border">
                                        <div>
                                          <p className="text-sm font-medium">{test.title}</p>
                                          <p className="text-xs text-gray-500">{formatDate(test.created_at)}</p>
                                        </div>
                                        <Badge variant={test.status === 'published' ? 'success' : 'secondary'} className="text-xs">
                                          {test.status}
                                        </Badge>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Recent Actions */}
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-3">Recent Activity</h4>
                                {report.recent_actions.length === 0 ? (
                                  <p className="text-sm text-gray-500">No recent activity</p>
                                ) : (
                                  <div className="space-y-2 max-h-64 overflow-y-auto">
                                    {report.recent_actions.map((action, idx) => (
                                      <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border text-sm">
                                        <div className="flex items-center gap-2">
                                          <Badge variant="outline" className="text-xs capitalize">
                                            {action.entity}
                                          </Badge>
                                          <span className="text-gray-700 capitalize">
                                            {action.action?.replace(/_/g, ' ')}
                                          </span>
                                        </div>
                                        <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                                          {formatDate(action.timestamp)}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Action Breakdown */}
                              {Object.keys(report.action_breakdown).length > 0 && (
                                <div className="lg:col-span-2">
                                  <h4 className="font-semibold text-gray-900 mb-3">Action Summary</h4>
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(report.action_breakdown).map(([action, count]) => (
                                      <div key={action} className="px-3 py-1.5 bg-white border rounded-full text-sm">
                                        <span className="text-gray-600 capitalize">{action.replace(/_/g, ' ')}</span>
                                        <span className="ml-2 font-semibold text-gray-900">{count}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}

export default FacultyReports;
